<template>
  <main class="invite-reward-page">
    <AppNavBar :title="t('invite.rewardDetails')" :border="false">
      <template #right>
        <button
          type="button"
          class="invite-reward-page__user"
          @click="router.push('/wallet/invite/recommended-users')"
        >
          <AppSvgIcon name="invite-user" size="24" />
        </button>
      </template>
    </AppNavBar>

    <section v-if="pageLoading" class="reward-page-skeleton" aria-busy="true">
      <div class="reward-page-skeleton__summary">
        <van-skeleton title :row="3" />
      </div>
      <div class="reward-page-skeleton__section">
        <van-skeleton title :row="1" />
        <van-skeleton title :row="4" />
      </div>
    </section>

    <template v-else>
      <section
        class="reward-summary"
        :class="{ 'reward-summary--partner': isPartner }"
      >
        <div class="reward-summary__head">
          <span>{{ totalCommissionLabel }}</span>
          <div class="reward-summary__level">
            <span>{{ currentLevelLabel }}</span>
            <AppSvgIcon
              v-if="levelMeta.svgIcon"
              class="reward-summary__level-icon"
              :name="levelMeta.svgIcon"
              color="var(--wp-color-brand-1)"
            />
            <app-image
              v-else
              class="reward-summary__level-icon"
              :src="levelMeta.icon"
              alt=""
            />
          </div>
        </div>

        <div class="reward-summary__amount">{{ totalCommissionAmount }}</div>
        <div class="reward-summary__divider" />

        <div class="reward-summary__stats">
          <div class="reward-summary__stat">
            <span>{{ t("invite.withdrawable") }}</span>
            <div>{{ withdrawableAmount }}</div>
          </div>
          <div class="reward-summary__stat">
            <button
              type="button"
              class="reward-summary__stat-label"
              @click="showPendingSettlementDialog = true"
            >
              {{ t("invite.pendingSettlement") }}
              <van-icon name="question-o" />
            </button>
            <div>{{ pendingSettlementAmount }}</div>
          </div>
          <div class="reward-summary__stat">
            <span>{{ t("invite.settled") }}</span>
            <div>{{ settledAmount }}</div>
          </div>
        </div>
      </section>

      <div class="reward-section-head">
        <h2>{{ t("invite.myRewards") }}</h2>
        <button type="button" @click="showRangeSheet = true">
          {{ t("invite.date") }}
        </button>
      </div>

      <section class="reward-content">
        <van-tabs v-model:active="activeTab" shrink class="reward-tabs">
          <van-tab :title="t('common.all')" :name="0" />
          <van-tab :title="t('invite.cardOpeningCommissionShort')" :name="1" />
          <van-tab
            v-if="isMonthlyCommissionLevel"
            :title="t('invite.monthlyCommissionTitle')"
            :name="11"
          />
          <van-tab :title="t('invite.consumptionCommission')" :name="17" />
        </van-tabs>

        <div class="pull-refresh">
          <van-pull-refresh v-model="refreshing" @refresh="refreshList">
            <van-list
              v-model:loading="loading"
              :finished="finished"
              :finished-text="''"
              :immediate-check="false"
              @load="loadList"
            >
              <section
                v-if="!rewardList.length && !loading"
                class="reward-empty"
              >
                <app-image src="@/assets/images/emptydata_gray.png" alt="" />
                <span>{{ t("invite.noRewardRecords") }}</span>
              </section>

              <section v-else class="reward-list">
                <button
                  v-for="item in rewardList"
                  :key="item.id"
                  type="button"
                  class="reward-record"
                  @click="openRewardDetail(item)"
                >
                  <div class="reward-record__main">
                    <div class="reward-record__title">
                      {{ rewardTitle(item) }}
                    </div>
                    <span>{{ formatTime(item.createAt) }}</span>
                  </div>

                  <div class="reward-record__side">
                    <div
                      class="reward-record__amount"
                      :class="amountClass(item)"
                    >
                      {{ formatRecordAmount(item) }}
                    </div>
                    <span
                      class="reward-record__status"
                      :class="statusClass(item)"
                    >
                      {{ statusText(item) }}
                    </span>
                  </div>

                  <app-image
                    class="reward-record__arrow"
                    src="@/assets/images/rightArrow_icon.png"
                    alt=""
                  />
                </button>
              </section>
            </van-list>
          </van-pull-refresh>
        </div>
      </section>
    </template>

    <van-popup
      v-model:show="showRangeSheet"
      round
      position="bottom"
      class="range-popup"
      @open="syncPendingRange"
    >
      <div class="range-popup__header">
        <div class="range-popup__title-wrap">
          <h3>{{ t("card.selectTimeRange") }}</h3>
          <p>{{ t("card.timeRangeTip") }}</p>
        </div>
      </div>

      <div class="range-popup__options">
        <button
          v-for="item in rangeActions"
          :key="item.value"
          type="button"
          class="range-popup__option"
          :class="{
            'range-popup__option--active': pendingRange === item.value,
          }"
          @click="pendingRange = item.value"
        >
          <span>{{ item.name }}</span>
          <AppSvgIcon
            v-if="pendingRange === item.value"
            class="range-popup__checked-icon"
            name="check-circle"
            color="var(--wp-color-theme)"
          />
          <van-icon v-else name="circle" color="var(--wp-color-theme)" />
        </button>
      </div>

      <div class="range-popup__footer">
        <button
          type="button"
          class="range-popup__action range-popup__action--reset"
          @click="resetRange"
        >
          {{ t("common.reset") }}
        </button>
        <button
          type="button"
          class="range-popup__action range-popup__action--confirm"
          @click="confirmRange"
        >
          {{ t("common.confirm") }}
        </button>
      </div>
    </van-popup>

    <van-popup
      v-model:show="showPendingSettlementDialog"
      class="pending-settlement-popup"
      overlay-class="pending-settlement-overlay"
      :close-on-click-overlay="false"
    >
      <section class="pending-settlement-dialog">
        <h2>{{ t("invite.pendingSettlementTitle") }}</h2>
        <p class="pending-settlement-dialog__description">
          {{ t("invite.pendingSettlementDescription") }}
        </p>
        <h3>{{ t("invite.pendingSettlementQuestion") }}</h3>
        <ul>
          <li v-if="isMonthlyCommissionLevel">
            {{ t("invite.pendingSettlementMonthlyCommission") }}
          </li>
          <li>{{ t("invite.pendingSettlementCardActivation") }}</li>
          <li>{{ t("invite.pendingSettlementTransaction") }}</li>
        </ul>
        <van-button
          block
          round
          type="primary"
          class="pending-settlement-dialog__confirm"
          @click="showPendingSettlementDialog = false"
        >
          {{ t("invite.purchaseCommissionConfirm") }}
        </van-button>
      </section>
    </van-popup>
  </main>
</template>

<script setup>
import dayjs from "dayjs";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { refOverview, refRewardList } from "@/api/wallet";
import { useProfileStore } from "@/stores/profile";
import { navigateWithState } from "@/utils/route-state";
import {
  normalizeRewardStatus,
  rewardAmountClass,
  rewardStatusClass,
  rewardStatusTextKey,
} from "@/utils/reward-status";
import pointCardTwoIcon from "@/assets/images/settingImage/pointCard_two_icon.png";
import pointCardThreeIcon from "@/assets/images/settingImage/pointCard_three_icon.png";
import pointCardFourIcon from "@/assets/images/settingImage/pointCard_four_icon.png";

const { locale, t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const REWARD_TYPE_TEXT_KEYS = Object.freeze({
  1: "invite.rewardRecordCardOpeningTitle",
  2: "invite.spendingCommission",
  3: "invite.commissionRefundDeduction",
  4: "invite.reservedReward",
  10: "invite.courseCommission",
  11: "invite.globalDirectorCourseCommission",
  12: "invite.globalDirectorCardOpeningCommission",
  13: "invite.teamBonusPoolCardOpeningCommission",
  14: "invite.teamBonusPoolCourseCommission",
  15: "invite.globalDirectorTeamCardOpeningCommission",
  16: "invite.globalDirectorTeamCourseCommission",
});
const activeTab = ref(0);
const refreshing = ref(false);
const rewardOverview = ref({});
const showRangeSheet = ref(false);
const showPendingSettlementDialog = ref(false);
const selectedRange = ref(30);
const pendingRange = ref(selectedRange.value);
const rewardList = ref([]);
const pageLoading = ref(true);
const loading = ref(false);
const finished = ref(false);
const pageNum = ref(1);
const pageSize = 20;
const requesting = ref(false);
let queuedListReset = false;
let rewardListVersion = 0;
const rewardSymbol = computed(
  () => String(rewardOverview.value?.symbol || "USDT").trim() || "USDT",
);
const totalCommissionLabel = computed(() =>
  t("invite.totalCommissionUsdt").replace("USDT", rewardSymbol.value),
);
const overviewLevels = computed(() => {
  const levels = Array.isArray(rewardOverview.value?.levels)
    ? rewardOverview.value.levels
    : [];
  return [...levels].sort(
    (left, right) => Number(left?.level || 0) - Number(right?.level || 0),
  );
});

const isPartner = computed(
  () => Number(currentOverviewLevel.value?.partner) === 1,
);
const currentOverviewLevel = computed(() => {
  const currentLevel = overviewLevels.value.find(
    (level) => Number(level?.current) === 1,
  );
  if (currentLevel) {
    return currentLevel;
  }
  const refLevel = Number(rewardOverview.value?.refLevel || 0);
  return (
    overviewLevels.value.find(
      (level) => Number(level?.level || 0) === refLevel,
    ) ||
    overviewLevels.value[0] || {
      level: 1,
      name: "LV.1",
      partner: 0,
    }
  );
});
const isMonthlyCommissionLevel = computed(() => {
  const level = Number(
    currentOverviewLevel.value?.level || rewardOverview.value?.refLevel || 0,
  );
  return level === 3 || level === 4;
});
const currentOverviewLevelIndex = computed(() =>
  Math.max(
    0,
    overviewLevels.value.findIndex(
      (level) => level === currentOverviewLevel.value,
    ),
  ),
);

const levelMeta = computed(() => {
  const level = currentOverviewLevel.value;
  const levelIndex = currentOverviewLevelIndex.value;
  const isPartner = Number(level?.partner) === 1;

  if (isPartner) {
    return {
      label: String(t("invite.partner")),
      icon: pointCardFourIcon,
      textColor: "#ffeaa3",
      background: "#4e4d4d",
      shadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
    };
  }
  if (levelIndex === 2) {
    return {
      label:
        t("mine.currentLevel") +
        String(level?.name || `LV.${level?.level || 3}`),
      icon: pointCardThreeIcon,
      textColor: "#333",
      textDefultColor: "#b15921",
      background: "linear-gradient(180deg, #fce8c3 0%, #ffffff 100%)",
      shadow: "0 4px 12px rgba(246, 198, 113, 0.2)",
    };
  }
  if (levelIndex === 1) {
    return {
      label:
        t("mine.currentLevel") +
        String(level?.name || `LV.${level?.level || 2}`),
      icon: pointCardTwoIcon,
      textColor: "#333",
      textDefultColor: "#234796",
      background: "linear-gradient(180deg, #aed9ff 0%, #ffffff 100%)",
      shadow: "0 4px 12px rgba(35, 71, 150, 0.12)",
    };
  }
  return {
    label:
      t("mine.currentLevel") +
      String(
        level?.name ||
          `LV.${level?.level || rewardOverview.value?.refLevel || 1}`,
      ),
    svgIcon: "point-card-one",
    textColor: "#333",
    textDefultColor: "var(--wp-color-brand-1)",
    background: "linear-gradient(180deg, #FBEBDD 0%, #ffffff 100%)",
    shadow: "0 4px 4px 0 #FBEDE0",
  };
});

const currentLevelLabel = computed(() => levelMeta.value.label);

const totalCommissionAmount = computed(() => rewardOverview.value?.totalAmount);
const withdrawableAmount = computed(
  () => rewardOverview.value?.claimableAmount,
);
const pendingSettlementAmount = computed(
  () => rewardOverview.value?.waitingAmount,
);
const settledAmount = computed(() => rewardOverview.value?.settledAmount);
const rangeActions = computed(() => [
  { name: t("card.last30Days"), value: 30 },
  { name: t("card.last90Days"), value: 90 },
  { name: t("card.last180Days"), value: 180 },
]);

function rewardTypeParam() {
  const type = Number(activeTab.value);
  return type > 0 ? type : undefined;
}

function normalizeRewardRecord(item = {}) {
  const type = Number(item.type ?? item.rewardType);
  const statusKey = normalizeRewardStatus(item.statusKey ?? item.status);
  return {
    ...item,
    id: item.id ?? `${item.createAt || ""}-${item.amount || ""}`,
    typeKey: type,
    statusKey,
    createTime: item.createAt,
    triggerTime: item.planAt,
    settlementTime: item.settleAt,
  };
}

function formatSummaryAmount(value) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : "0.00";
}

function formatRecordAmount(item = {}) {
  const amount = Number(item.amount);
  const symbol =
    String(item.symbol || rewardSymbol.value).trim() || rewardSymbol.value;
  if (!Number.isFinite(amount)) {
    return `0 ${symbol}`;
  }
  const compactAmount = Number.isInteger(Math.abs(amount))
    ? String(Math.abs(amount))
    : String(Number(Math.abs(amount).toFixed(8)));
  const sign = amount > 0 ? "+" : amount < 0 ? "-" : "";
  return `${sign}${compactAmount} ${symbol}`;
}

function formatTime(value) {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  const numericValue = Number(value);
  const dateValue = Number.isFinite(numericValue)
    ? numericValue < 10000000000
      ? numericValue * 1000
      : numericValue
    : value;
  const date = dayjs(dateValue);
  return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "";
}

function amountClass(item) {
  return rewardAmountClass(item.statusKey ?? item.status);
}

function statusClass(item) {
  return rewardStatusClass(item.statusKey ?? item.status);
}

function statusText(item) {
  return t(rewardStatusTextKey(item.statusKey ?? item.status));
}

function openRewardDetail(item) {
  navigateWithState(router, "/wallet/invite/reward/detail", {
    namespace: "wallet-invite-reward-detail",
    payload: item,
  });
}

function rewardTitle(item) {
  const textKey = REWARD_TYPE_TEXT_KEYS[Number(item.typeKey)];
  return textKey
    ? item.refDepth === 2
      ? t("invite.rewardRecordCourseCommissionTitle", {
          friendName: item.friendName || "--",
        })
      : item.typeKey === 1
        ? t(textKey, {
            friendName: item.friendName || "--",
          })
        : t(textKey)
    : "--";
}

function syncPendingRange() {
  pendingRange.value = selectedRange.value;
}

function resetRange() {
  pendingRange.value = 30;
}

function confirmRange() {
  selectedRange.value = pendingRange.value;
  showRangeSheet.value = false;
  loadList(true);
}

async function loadList(reset = false) {
  if (reset) {
    rewardListVersion += 1;
    rewardList.value = [];
    pageNum.value = 1;
    finished.value = false;
  }

  if (requesting.value) {
    queuedListReset = queuedListReset || reset;
    return;
  }

  if (!reset && finished.value) {
    return;
  }

  const requestVersion = rewardListVersion;
  const requestPageNum = pageNum.value;
  requesting.value = true;
  loading.value = true;
  try {
    const result = await refRewardList(
      {
        pageNum: requestPageNum,
        pageSize,
        type: rewardTypeParam(),
        lastDays: selectedRange.value,
      },
      { meta: { showLoading: false } },
    );
    const rows = Array.isArray(result?.list)
      ? result.list.map(normalizeRewardRecord)
      : [];
    const responsePageNum = Number(result?.pageNum || pageNum.value);
    const responsePages = Number(result?.pages || 0);

    if (requestVersion !== rewardListVersion) {
      return;
    }

    rewardList.value =
      requestPageNum === 1 ? rows : [...rewardList.value, ...rows];
    pageNum.value = responsePageNum + 1;
    finished.value =
      responsePages > 0
        ? responsePageNum >= responsePages
        : rows.length < pageSize;
  } catch (error) {
    // The HTTP client already displays the request error.
  } finally {
    requesting.value = false;
    loading.value = false;
    if (queuedListReset) {
      queuedListReset = false;
      void loadList(true);
    }
  }
}

async function loadRewardOverview() {
  const address = profileStore.platformAddress || null;
  if (!address) {
    rewardOverview.value = {};
    return;
  }

  try {
    rewardOverview.value =
      (await refOverview(
        {
          address,
          lang: locale.value || profileStore.locale || "zh-CN",
        },
        { meta: { showLoading: false } },
      )) || {};
  } catch (error) {
    rewardOverview.value = {};
  }
}

async function refreshList() {
  await Promise.all([loadList(true), loadRewardOverview()]);
  refreshing.value = false;
}

async function loadPage() {
  pageLoading.value = true;
  try {
    await Promise.all([loadList(true), loadRewardOverview()]);
  } finally {
    pageLoading.value = false;
  }
}

watch(activeTab, () => {
  void loadList(true);
});

watch(locale, () => {
  loadRewardOverview();
});

onMounted(() => {
  void loadPage();
});
</script>

<style scoped lang="scss">
.invite-reward-page {
  min-height: var(--app-viewport-height);
  background: #f5f5f5;
}

.invite-reward-page__user {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;

  .app-image,
  .app-svg-icon {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
}

.reward-page-skeleton {
  min-height: calc(var(--app-viewport-height) - var(--van-nav-bar-height));
  padding: 12px 20px 28px;
  background: #ffffff;
}

.reward-page-skeleton__summary {
  padding: 16px 20px;
  border-radius: 16px;
  background: #f5f5f5;
}

.reward-page-skeleton__section {
  margin-top: 20px;
  padding: 16px 0;
  border-radius: 18px 18px 0 0;
  background: #ffffff;
}

.reward-page-skeleton :deep(.van-skeleton) {
  padding: 0;
}

.reward-summary {
  margin: 12px 20px 0;
  padding: 16px 20px 15px;
  border-radius: 16px;
  background: v-bind("levelMeta.background");
  box-shadow: v-bind("levelMeta.shadow");
  color: v-bind("levelMeta.textColor");
}

.reward-summary__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  line-height: 20px;
}

.reward-summary__level {
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;

  .reward-summary__level-icon {
    width: 18px;
    height: 16px;
    object-fit: contain;
  }
  span {
    color: v-bind("levelMeta.textDefultColor");
  }
}

.reward-summary__amount {
  margin-top: 5px;
  color: v-bind("levelMeta.textColor");
  font-size: 20px;
  font-weight: 500;
  line-height: 34px;
}

.reward-summary__divider {
  height: 0;
  margin: 10px 0 13px;
  border-top: 1px dashed rgba(38, 103, 255, 0.18);
}

.reward-summary--partner .reward-summary__divider {
  border-top-color: #ffffff;
}

.reward-summary__stats {
  color: v-bind("levelMeta.textColor");
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.reward-summary__stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  text-align: center;

  > span,
  > button {
    display: inline-flex;
    align-items: center;
    // min-height: 18px;
    font-size: 12px;
    // line-height: 18px;
  }

  > div {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    margin-top: 1px;
    color: v-bind("levelMeta.textColor");
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    overflow-wrap: anywhere;
    word-break: break-word;
    white-space: normal;
  }
}

.reward-summary__stat-label {
  position: relative;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;

  .van-icon {
    position: absolute;
    top: 50%;
    left: calc(100% + 3px);
    font-size: 13px;
    transform: translateY(-50%);
  }
}

.reward-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 20px 14px;

  h2 {
    margin: 0;
    color: #111111;
    font-size: 17px;
    font-weight: 700;
    line-height: 24px;
  }

  button {
    min-width: 62px;
    height: 30px;
    padding: 0 14px;
    border: 0;
    border-radius: 18px;
    background: #ffffff;
    color: #333333;
    font-size: 13px;
  }
}

.reward-content {
  min-height: calc(var(--app-viewport-height) - 286px);
  overflow: hidden;
  border-radius: 18px 18px 0 0;
  background: #ffffff;
}

.reward-tabs {
  padding: 10px 20px 0;
  background: #ffffff;

  :deep(.van-tabs__wrap) {
    height: 40px;
  }

  :deep(.van-tabs__nav--line) {
    gap: 0;
    padding-bottom: 10px;
  }

  :deep(.van-tab) {
    flex: none;
    margin-right: 20px;
    padding: 0 0 0px;
    color: #666;
    font-size: 16px;
  }

  :deep(.van-tab:first-child) {
    padding-left: 0;
  }

  :deep(.van-tab--active) {
    color: #000000;
    font-weight: 700;
  }

  :deep(.van-tabs__line) {
    bottom: 12px;
    width: 26px;
    height: 3px;
    margin-top: 0px;
    border-radius: 30px;
    background: var(--wp-color-brand-1);
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.04);
  }
}

.pull-refresh {
  min-height: calc(var(--app-viewport-height) - 334px);
  background: #ffffff;
}

.reward-list {
  padding: 10px 20px 28px;
  margin-top: 12px;
}

.reward-record {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 16px;
  align-items: center;
  width: 100%;
  gap: 8px;
  min-height: 68px;
  box-sizing: border-box;
  margin-bottom: 10px;
  padding: 12px;
  border: 0;
  border-radius: 12px;
  background: #f5f5f5;
  text-align: left;
}

.reward-record__main {
  min-width: 0;

  > span {
    display: block;
    margin-top: 2px;
    color: #a1a1a1;
    font-size: 13px;
    line-height: 18px;
  }
}

.reward-record__title {
  display: -webkit-box;
  overflow: hidden;
  color: #000;
  font-size: 14px;
  font-weight: 500;
  overflow-wrap: anywhere;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  margin-bottom: 10px;
}

.reward-record__side {
  min-width: 82px;
  text-align: right;
}

.reward-record__amount {
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  white-space: nowrap;
  margin-bottom: 3px;

  &.is-pending {
    color: var(--wp-color-theme);
  }

  &.is-claimable {
    color: var(--wp-reward-claimable);
  }

  &.is-success {
    color: var(--wp-reward-success);
  }

  &.is-deducted {
    color: var(--wp-reward-deducted);
  }
}

.reward-record__status {
  display: block;
  margin-top: 1px;
  font-size: 13px;
  line-height: 18px;

  &.is-claimable {
    color: var(--wp-color-brand-1);
  }

  &.is-success {
    color: #666;
  }

  &.is-pending {
    color: var(--wp-color-theme);
  }

  &.is-deducted {
    color: #f11c35;
  }
}

.reward-record__arrow {
  width: 9px;
  height: 14px;
  flex: 0 0 9px;
  object-fit: contain;
}

.reward-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 70px 0 40px;
  color: #999999;
  font-size: 14px;

  .app-image {
    width: 100px;
    height: 100px;
    margin-bottom: 8px;
    object-fit: contain;
  }
}

.range-popup {
  overflow: hidden;
  background: #ffffff;
}

.range-popup__header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px 0px 10px;
}

.range-popup__placeholder,
.range-popup__close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 24px;
}

.range-popup__close {
  padding: 0;
  border: 0;
  background: transparent;
  color: #999999;
  font-size: 20px;
}

.range-popup__title-wrap {
  flex: 1;
  text-align: center;

  h3 {
    margin: 0;
    color: #111111;
    font-size: 18px;
    font-weight: 700;
    line-height: 26px;
  }

  p {
    margin: 4px 0 0;
    color: #999999;
    font-size: 13px;
    line-height: 18px;
  }
}

.range-popup__options {
  padding: 0 0 12px;
}

.range-popup__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 54px;
  padding: 0 18px;
  border: 0;
  background: #ffffff;
  color: #666;
  text-align: left;

  span {
    font-size: 16px;
    line-height: 22px;
  }

  .van-icon {
    color: var(--wp-color-theme);
    font-size: 20px;
  }
}

.range-popup__checked-icon {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
}

.range-popup__option--active {
  background: var(--wp-color-card-1);
  color: #000;
  font-weight: 500;
}

.range-popup__footer {
  display: flex;
  gap: 16px;
  padding: 8px 16px calc(20px + env(safe-area-inset-bottom));
}

.range-popup__action {
  flex: 1;
  height: 44px;
  border: 0;
  border-radius: 22px;
  font-size: 18px;
  font-weight: 500;
}

.range-popup__action--reset {
  background: var(--wp-color-card-1);
  color: var(--wp-color-theme);
}

.range-popup__action--confirm {
  background: var(--wp-color-theme);
  color: #ffffff;
}

:global(.pending-settlement-popup.van-popup--center) {
  width: calc(100% - 48px);
  max-width: 344px;
  overflow: hidden;
  border-radius: 24px;
  background: #ffffff;
}

:global(.pending-settlement-overlay) {
  width: var(--app-shell-width);
  max-width: 100vw;
  left: var(--app-shell-left);
  right: auto;
}

.pending-settlement-dialog {
  padding: 24px 20px 26px;
  background: #ffffff;

  h2,
  h3 {
    margin: 0;
  }

  h2 {
    font-size: 18px;
    color: #000000;
    font-weight: 700;
    line-height: 31px;
  }

  h3 {
    margin-top: 20px;
    font-size: 18px;
    font-weight: 500;
    line-height: 26px;
    color: #333;
  }

  ul {
    margin: 12px 0 0;
    padding: 0;
    list-style: none;
  }

  li {
    position: relative;
    padding-left: 16px;
    color: #333333;
    font-size: 14px;
    line-height: 30px;
    font-weight: 500;

    &::before {
      position: absolute;
      top: 10px;
      left: 0;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #000000;
      content: "";
    }
  }
}

.pending-settlement-dialog__description {
  margin: 22px 0 0;
  color: #333333;
  font-size: 16px;
  font-weight: 500;
}

.pending-settlement-dialog__confirm {
  margin-top: 28px;
  border: 0;
  background: var(--wp-color-theme);
  font-size: 18px;
  font-weight: 500;
}
</style>
